import { CalendarEvent } from '../../types/calendar';
import { isMultiDayEvent, isAllDayEvent, getEventCardStyle } from '../../utils/calendar';
import { formatRecurringPattern } from '../../utils/recurrence';
import { getDateTypeIcon, getImportantDateBorder, getPriorityBadgeColor } from '../../utils/eventAppearance';
import { formatTime } from '../../utils/time';
import MemberAvatar from '../MemberAvatar';
import { Icon } from '../../utils/icons';

interface EventCardProps {
  event: CalendarEvent;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'header';
  showMultiDayIndicator?: boolean;
  isPast?: boolean;
}

// Backward compatible export used by other components (e.g., DayView).
export { getDateTypeIcon };

export default function EventCard({ event, onEventClick, isDarkMode = false, size = 'md', variant = 'default', showMultiDayIndicator = true, isPast = false }: EventCardProps) {
  const isMultiDay = isMultiDayEvent(event.startTime, event.endTime);
  
  // Support both legacy (single member) and new (multiple members) format
  const members = event.members && event.members.length > 0
    ? event.members.map(m => ({
        ...m,
        memberColor: m.memberColor || '#3b82f6', // Ensure memberColor is always set
      }))
    : (event.memberId ? [{
        memberId: event.memberId,
        memberName: event.memberName || '',
        memberColor: event.memberColor || '#3b82f6',
        memberAvatar: event.memberAvatar,
      }] : []);

  const dateTypeIcon = getDateTypeIcon(event.dateType);
  const priorityBadgeColor = getPriorityBadgeColor(event.priority, isDarkMode);
  const importantDateBorder = event.isImportantDate
    ? getImportantDateBorder(event.dateType, event.priority, isDarkMode)
    : '';
  const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';

  const sizeClasses = {
    sm: {
      container: isPast ? 'text-[9px] sm:text-[10px] px-0.5 py-0.5 sm:py-0.5 min-h-[60px]' : 'text-[10px] sm:text-xs px-1 py-0.5 sm:py-1 min-h-[80px]',
      title: isPast ? 'text-[9px] sm:text-[10px]' : 'text-[10px] sm:text-xs',
      time: isPast ? 'text-[8px] sm:text-[9px]' : 'text-[9px] sm:text-[10px]',
      gap: isPast ? 'gap-0.5' : 'gap-0.5 sm:gap-1',
      avatarSize: 'sm' as const,
    },
    md: {
      container: isPast ? 'text-[10px] sm:text-xs px-1 py-0.5 sm:py-0.5 min-h-[60px]' : 'text-xs sm:text-sm lg:text-base px-1 py-0.5 sm:py-1 min-h-[80px]',
      title: isPast ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm lg:text-base',
      time: isPast ? 'text-[9px] sm:text-[10px]' : 'text-[10px] sm:text-xs',
      gap: isPast ? 'gap-0.5 sm:gap-1' : 'gap-1 sm:gap-2',
      avatarSize: isPast ? 'sm' as const : 'md' as const,
    },
    lg: {
      container: isPast ? 'text-xs sm:text-sm px-1.5 sm:px-2 py-1 sm:py-1.5 min-h-[70px]' : 'text-sm sm:text-base px-2 sm:px-3 py-2 sm:py-3 min-h-[90px]',
      title: isPast ? 'text-xs sm:text-sm' : 'text-sm sm:text-base',
      time: isPast ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm',
      gap: isPast ? 'gap-1' : 'gap-1.5 sm:gap-2',
      avatarSize: 'md' as const,
    },
  };

  const sizeConfig = sizeClasses[size];

  // Header variant: horizontal, compact layout for multi-day events in header
  if (variant === 'header') {
    const headerStyle = getEventCardStyle(members, isDarkMode);
    const hasLeftBar = headerStyle.leftBarGradient;
    const leftBarWidth = headerStyle.leftBarWidth ? parseInt(headerStyle.leftBarWidth.replace('px', '')) : 0;
    const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 4}px` : undefined;
    
    // Ensure border color is consistent - use explicit border color in style to avoid any visual differences
    // For important dates, keep using Tailwind classes; for regular events, use explicit peachy color
    const defaultBorderColorHeader = isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(141, 125, 107, 0.25)'; // Peachy warm tone
    
    return (
      <div
        className={`px-2 py-1.5 rounded-landing cursor-pointer touch-manipulation active:scale-95 flex items-center gap-2 h-full relative ${isDarkMode ? 'text-gray-100' : 'text-gray-600'} border-2 ${importantDateBorder || ''} overflow-hidden ${event.isImportantDate && !isMultiDay ? 'shadow-md' : ''}`}
        style={{
          backgroundColor: headerStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          background: headerStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          paddingLeft: paddingLeft,
          ...(importantDateBorder ? {} : { borderColor: defaultBorderColorHeader }),
        }}
        onClick={(e) => {
          e.stopPropagation();
          onEventClick?.(event);
        }}
        title={event.title}
      >
        {/* Left bar gradient overlay for multi-member events */}
        {hasLeftBar && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{
              width: headerStyle.leftBarWidth || '4px',
              background: headerStyle.leftBarGradient,
              borderTopLeftRadius: '18px', // Match rounded-landing (18px)
              borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
              zIndex: 1,
            }}
          />
        )}
        {/* Multi-day indicator: Top-left corner triangle */}
        {isMultiDay && showMultiDayIndicator && (
          <div
            className="absolute top-0 left-0 pointer-events-none"
            style={{
              width: 0,
              height: 0,
              borderTop: '20px solid #fbbf24', // Amber color
              borderRight: '20px solid transparent',
              zIndex: 10,
            }}
          />
        )}
        
        {/* Date type icon and recurring indicator */}
        <div className="flex-shrink-0 flex items-center gap-1 relative z-10">
          {dateTypeIcon && (
            <span title={event.dateType || undefined}>
              <Icon name={dateTypeIcon} size="sm" className={isDarkMode ? 'text-gray-100' : 'text-gray-600'} />
            </span>
          )}
          {event.isRecurring && (
            <span className="text-[10px] sm:text-xs opacity-75" title={recurringPatternText}>↻</span>
          )}
        </div>

        {/* Member Avatars - Horizontal layout */}
        <div className="flex-shrink-0 flex items-center gap-1 relative z-10">
          {members.map((member, idx) => (
            <MemberAvatar
              key={member.memberId || idx}
              member={member}
              size="sm"
            />
          ))}
        </div>
        
        {/* Title - Truncated for compact header */}
        <div className="flex-1 min-w-0 relative z-10">
          <div className={`font-medium text-xs sm:text-sm truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`}>
            {event.title}
          </div>
        </div>

        {/* Priority badge */}
        {priorityBadgeColor && (
          <div className={`flex-shrink-0 px-1 py-0.5 rounded-sm text-[9px] sm:text-[10px] font-semibold border ${priorityBadgeColor} relative z-10`}>
            {event.priority?.toUpperCase()}
          </div>
        )}
      </div>
    );
  }

  // Default variant: vertical layout
  const cardStyle = getEventCardStyle(members, isDarkMode);
  const hasLeftBar = cardStyle.leftBarGradient;
  const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
  const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 4}px` : undefined;
  
  // Ensure border color is consistent - use explicit border color in style to avoid any visual differences
  // For important dates, keep using Tailwind classes; for regular events, use explicit peachy color
  const defaultBorderColor = isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(141, 125, 107, 0.25)'; // Peachy warm tone
  
  return (
    <div
      className={`${sizeConfig.container} h-full rounded-landing cursor-pointer touch-manipulation active:scale-95 flex flex-col ${sizeConfig.gap} mb-0.5 sm:mb-1 relative ${isDarkMode ? 'text-gray-100' : 'text-gray-600'} border-2 ${importantDateBorder || ''} overflow-hidden ${event.isImportantDate && !isMultiDay ? 'shadow-md' : ''}`}
      style={{
        backgroundColor: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
        background: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
        paddingLeft: paddingLeft,
        ...(importantDateBorder ? {} : { borderColor: defaultBorderColor }),
      }}
      onClick={(e) => {
        e.stopPropagation();
        onEventClick?.(event);
      }}
      title={event.title}
    >
      {/* Left bar gradient overlay for multi-member events */}
      {hasLeftBar && (
        <div
          className="absolute left-0 top-0 bottom-0"
          style={{
            width: cardStyle.leftBarWidth || '4px',
            background: cardStyle.leftBarGradient,
            borderTopLeftRadius: '18px', // Match rounded-landing (18px)
            borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
            zIndex: 1,
          }}
        />
      )}
      {/* Multi-day indicator: Top-left corner triangle */}
      {isMultiDay && showMultiDayIndicator && (
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            width: 0,
            height: 0,
            borderTop: isDarkMode 
              ? '20px solid rgba(251, 191, 36, 0.3)' // Subtle amber for dark mode
              : '20px solid rgba(251, 191, 36, 0.25)', // Subtle amber for light mode
            borderRight: '20px solid transparent',
            zIndex: 10,
          }}
        />
      )}
      
      {/* Text content - Always on top */}
      <div className="flex-1 min-w-0 relative z-10">
        <div className="flex items-center gap-1.5 mb-0.5">
          {dateTypeIcon && (
            <span title={event.dateType || undefined}>
              <Icon name={dateTypeIcon} size="sm" className={isDarkMode ? 'text-gray-100' : 'text-gray-600'} />
            </span>
          )}
          {event.isRecurring && (
            <span className={`${sizeConfig.time} opacity-75`} title={recurringPatternText}>↻</span>
          )}
          {priorityBadgeColor && (
            <span className={`px-1 py-0.5 rounded-sm text-[9px] sm:text-[10px] font-semibold border ${priorityBadgeColor}`}>
              {event.priority?.toUpperCase()}
            </span>
          )}
        </div>
        {(() => {
          const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
          const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
          const isAllDay = isAllDayEvent(eventStart, eventEnd, event.isAllDay);
          
          if (isAllDay) return null;
          
          return (
            <div className={`${sizeConfig.time} font-normal opacity-90 ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>
              {formatTime(eventStart)}
              {eventEnd && (
                <>
                  {' - '}
                  {formatTime(eventEnd)}
                </>
              )}
            </div>
          );
        })()}
        <div className={`font-medium ${sizeConfig.title} ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`} style={{ 
          minWidth: '50ch',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}>
          {event.title}
        </div>
      </div>
      
      {/* Member Avatars - Below text */}
      <div className={`flex-shrink-0 flex flex-row items-center justify-start ${sizeConfig.gap} relative z-10`}>
        {members.map((member, idx) => (
          <MemberAvatar
            key={member.memberId || idx}
            member={member}
            size={sizeConfig.avatarSize}
          />
        ))}
      </div>
    </div>
  );
}
